import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import useDateFormatter from 'src/hooks/useDateFormatter';
import ReportKeys from 'src/components/lab-report-verification/ReportKeys';
import IndividualChoice from 'src/components/lab-report-verification/IndividualChoice';
import {
  OTHERS_MARKED_TEXT,
  forWhomOf,
  isAnotherIndividualsRow,
  testIsForText
} from 'src/components/lab-report-verification/whichIndividual';
import { individualNoun } from 'src/components/lab-report-inbox/labReportInboxDisplay';
import {
  IDENTITY_ACKNOWLEDGEMENT,
  IDENTITY_MAY_BE_SOMEONE_ELSES_LEAD,
  identityCheckOf,
  identityCheckText
} from './labReportDisplay';
import { variantInterpretationText } from '../clinical-notes-section/forms/formService';
import {
  toLocalDate,
  toLocalDateFromPickerOrImport
} from 'src/util/partialDate';
import {
  GENES_TESTED_LIST_LABELS,
  METHOD_KIND_LABELS,
  SCOPE_LABELS,
  TEST_SCOPE_LABELS,
  ZYGOSITY_LABELS,
  rowSymbolOf,
  statedValueText
} from '../clinical-notes-section/forms/test-record/testRecordModel';
import {
  FIELD_LABELS,
  READING_WARNINGS,
  conflictsOf,
  incompleteKind,
  variantLabel,
  warningsOfReading
} from './applyProposal';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// A lab report uploaded on a Test (or handed over from the Lab Report Inbox), APPLIED TO THE TEST
// FORM the moment it is read (TGCLOUD-1866, Phase 2 rework: "the Test record is the home of the
// result"; TGCLOUD-1868 plan D3: applied on open, confirmed by group).
//
// This panel heads the Test pane while the report is checked beside it. It says where the report
// came from, asks for what concerns the report as a whole (the wrong-patient acknowledgement, the
// reading warnings), lists what saving will fill, add and merge ("Applying to this Test"), and
// shows each CONTRADICTION between the report and the Test side by side. The form keeps the Test's
// value until the clinician takes the report's, item by item (applyProposal.withChoice), and can
// change their mind; "Keep all this Test's" keeps every one. Nothing takes every one of the
// report's at once: each is a reading to check.
//
// Each part of the reading is confirmed where it is on the Test (the group cards), and what the
// reader did not read is in the footer (VerificationFooter). APPLYING WRITES NOTHING: SAVING the
// Test records the report (PUT /clinical-notes/test with labReportApplication), and closing the
// form without saving discards it. Dismiss takes the report off the form again.
//
// THE LAB REPORT INBOX (Phase 3). A review whose `origin` is 'inbox' was handed over from the
// inbox, where a person matched the report to this patient; it applies exactly as an upload does,
// and a refusal of the hand-off is said with what happened and the server's own next step. An
// UPLOADED report that reads as someone else's offers "Send to the inbox instead" beside the
// wrong-patient acknowledgement (when the caller has the inbox: `onSendToInbox`), so it can be
// matched to the right patient there rather than acknowledged onto this one. A report that came
// FROM the inbox offers no such thing: its match is undone in the inbox.
//
// ONE PERSON OF A REPORT ABOUT SEVERAL (TGCLOUD-1868, Phase E4). An uploaded report that gives results
// for two or more people is not read into the Test until the clinician says whose the Test is
// (a `choosing` review: IndividualChoice, nothing preselected); it is then read again for that
// person, and applied as any other reading. A reading for one person (chosen here, or handed over
// from the inbox) says so at its head, "This Test is for: Mother", while the report pane draws
// everyone else's rows apart (whichIndividual.markOtherIndividualRows).

const PANEL_SX = {
  p: 2,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1
};
const LABEL_SX = { color: 'text.secondary', fontSize: 12, fontWeight: 600 };
const VERBATIM_SX = { whiteSpace: 'pre-wrap', wordBreak: 'break-word' };
const SUBHEAD_SX = { fontWeight: 700, mt: 1.5, mb: 0.5 };
// A long list of differences shows this many until asked for the rest.
const SHOWN = 10;

// ── How each kind of value reads ──────────────────────────────────────────────────────────────
//
// Each takes the component's `t`: a label from the Test record's vocabulary is translated, and what
// the report printed is shown as it was printed.

const testScopeText = (scope, t) =>
  scope?.kind
    ? `${t(TEST_SCOPE_LABELS[scope.kind]) || scope.kind}${
        scope.asPrinted ? ` (${scope.asPrinted})` : ''
      }`
    : '';

// A gene row's coverage, as a phrase among the row's other details.
const COVERAGE_PHRASES = Object.freeze({
  complete: 'coverage complete',
  incomplete: 'coverage incomplete',
  'not-stated': 'coverage not stated'
});

const geneRowText = (row, t) =>
  [
    t(SCOPE_LABELS[row?.scope]) || row?.scope,
    t(
      COVERAGE_PHRASES[row?.coverage?.status] || COVERAGE_PHRASES['not-stated']
    ),
    (row?.methods || []).length
      ? row.methods
          .map((m) => t(METHOD_KIND_LABELS[m.kind] || METHOD_KIND_LABELS.other))
          .join(', ')
      : t('methods not stated'),
    row?.transcript
  ]
    .filter(Boolean)
    .join(' · ');

// A stated origin, as a phrase among the variant's other details.
const ORIGIN_PHRASES = Object.freeze({
  germline: 'germline origin',
  somatic: 'somatic origin'
});

// A variant as each side of a choice shows it. The origin is shown when stated: it travels to the
// genotype record with the variant, and a choice must not change it unseen.
const variantText = (v, t) =>
  [
    [v?.variantC, v?.variantP, v?.variantG].filter(Boolean).join(' '),
    v?.variantInterpretation
      ? variantInterpretationText(v.variantInterpretation, t)
      : t('no interpretation'),
    v?.origin && v.origin !== 'unknown'
      ? ORIGIN_PHRASES[v.origin]
        ? t(ORIGIN_PHRASES[v.origin])
        : t('{{origin}} origin', { origin: v.origin })
      : null,
    [v?.transcript, v?.genomeBuild].filter(Boolean).join(' · '),
    statedValueText(v?.zygosity, t)
  ]
    .filter(Boolean)
    .join(' — ');

// What a difference between the Test and the report is about: the Test record's own label, or an
// HGVS prefix (c., g., p.), which is the same in every language.
const differenceText = (label, t) =>
  /^[a-z]\.$/.test(label) ? label : t(label);

// "Adds 3 genes to genes tested." and "Fills in 1 gene already listed with nothing stated.": what
// saving does to genes tested, a sentence for one gene and one for more.
const GENE_PLAN_TEXT = Object.freeze({
  add: [
    'Adds 1 gene to genes tested.',
    'Adds {{count}} genes to genes tested.'
  ],
  fill: [
    'Fills in 1 gene already listed with nothing stated.',
    'Fills in {{count}} genes already listed with nothing stated.'
  ]
});

// ── One difference, both sides ────────────────────────────────────────────────────────────────

function Choice({ item, label, current, proposed, choice, onChoose }) {
  const t = useAutoTranslation();

  return (
    <Box
      data-testid={`conflict-${item.id}`}
      sx={{
        border: '1px solid',
        borderColor: 'warning.light',
        borderRadius: 1,
        p: 1,
        mb: 1
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      {item.differences?.length > 0 && (
        <Typography
          variant="caption"
          component="div"
          sx={{ color: 'text.secondary' }}
        >
          {t('Differs in: {{differences}}', {
            differences: item.differences
              .map((d) => differenceText(d, t))
              .join(', ')
          })}
        </Typography>
      )}
      <RadioGroup
        aria-label={t('Choose for {{label}}', { label })}
        value={choice || ''}
        onChange={(e) => onChoose({ [item.id]: e.target.value })}
      >
        <Grid container spacing={1}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              value="current"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography component="div" sx={LABEL_SX}>
                    {t('Keep this Test’s')}
                  </Typography>
                  <Typography component="div" variant="body2" sx={VERBATIM_SX}>
                    {current || '—'}
                  </Typography>
                </Box>
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              value="report"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography component="div" sx={LABEL_SX}>
                    {t('Use the report’s')}
                  </Typography>
                  <Typography component="div" variant="body2" sx={VERBATIM_SX}>
                    {proposed || '—'}
                  </Typography>
                </Box>
              }
            />
          </Grid>
        </Grid>
      </RadioGroup>
    </Box>
  );
}

// ── What the reader could not take from the report ────────────────────────────────────────────

// What a note-only incomplete item says: in the form's words where the path is a Test field. A
// genome build or a transcript the report lacks is entered on its variant (the variant's group
// card), and the Test result in Result (the overall result's): neither is repeated here.
const NOTE_COPY = Object.freeze({
  lab: 'The laboratory was not found in the report. Enter it under Result.',
  testName:
    'The name of the test was not found in the report. Enter it under Request.',
  resultSummaryAsPrinted: 'No conclusion was found in the report.'
});
const ENTERED_ON_THE_FORM =
  /^(variants\.\d+\.(genomeBuild|transcript)|testResultSuggestion)$/;

// A variant's value the application emptied because the reader was not confident of it, in words.
const CLEARED_FIELD_TEXT = Object.freeze({
  variantC: 'the c. description',
  variantP: 'the p. description',
  variantG: 'the g. description',
  transcript: 'the transcript',
  genomeBuild: 'the genome build',
  zygosity: 'the zygosity',
  variantInterpretation: 'the variant interpretation',
  'labClassification.classificationDate': 'the classification date'
});

const namesPatientText = (item, t) =>
  t(
    '{{value}} names the patient. The lab report record keeps no names and will not keep it; edit it on the Test if the Test should not keep it either.',
    { value: t(FIELD_LABELS[item.path]) || t('This text') }
  );

// ── The panel ─────────────────────────────────────────────────────────────────────────────────

// Where a review's report came from, said once at its head.
const ORIGIN_TITLE = Object.freeze({
  inbox: 'Lab report, from the Lab Report Inbox'
});

export const OTHER_APPLIED =
  'Another lab report is applied to this Test. Save the Test to record it, then open the Test again to apply this one.';

/**
 * @param {object} props
 * @param {object} props.review        one review from useLabReportReview
 * @param {boolean} [props.otherApplied]  another report is applied and not yet saved: this one
 *   waits (one report per save)
 * @param {Function} [props.onSendToInbox] () => void: "Send to the inbox instead", offered only
 *   for an uploaded report that reads as someone else's, and only when given
 * @param {Function} props.onAcknowledge (boolean) => void
 * @param {Function} props.onAcknowledgeWarning (READING_WARNINGS key, boolean) => void
 * @param {Function} props.onChoose    ({ [choiceId]: 'current'|'report' }) => void: made on the form
 * @param {Function} props.onDismiss   () => void: the report comes off the form
 * @param {Function} [props.onChooseIndividual] ({ key, role }) => void: whose part of a report about
 *   several people this Test is, chosen (a `choosing` review)
 * @param {Function} [props.onSendChoiceToInbox] ({ key, role } | null) => void: "Send to the inbox
 *   instead" while that is still chosen, sent from the reading for the person chosen (IndividualChoice);
 *   offered only when given
 * @param {{ pregnancyId?, fetusId? }} [props.testScope] the Test's prenatal scope, which says whose
 *   part of such a report it can take
 */
export default function LabReportApply({
  review,
  otherApplied = false,
  onAcknowledge,
  onAcknowledgeWarning = () => {},
  onChoose,
  onDismiss,
  onSendToInbox,
  onChooseIndividual = () => {},
  onSendChoiceToInbox,
  testScope = null
}) {
  const t = useAutoTranslation();

  const fmtDate = useDateFormatter();
  const [showAllConflicts, setShowAllConflicts] = React.useState(false);
  if (!review) return null;

  const fromInbox = review.origin === 'inbox';
  const title = (
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
      {t(ORIGIN_TITLE[review.origin]) || t('Lab report')}
    </Typography>
  );
  // Whom a reading of one person of a report about several is for (TGCLOUD-1868, Phase E4). That the
  // other people's rows are marked is said only where the reading marks some: a single-fetus report's
  // fetus is read whole, with nobody else's row (TGCLOUD-1868 closeout review).
  const forWhom = forWhomOf(review);
  const othersMarked = (review?.reading?.notRead || []).some(
    isAnotherIndividualsRow
  );
  const forWhomLine = forWhom ? (
    <Box data-testid="test-is-for" sx={{ mb: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {testIsForText(forWhom)}
      </Typography>
      {othersMarked && (
        <Typography
          variant="caption"
          component="div"
          sx={{ color: 'text.secondary' }}
        >
          {t(OTHERS_MARKED_TEXT)}
        </Typography>
      )}
    </Box>
  ) : null;
  const dismissButton = (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Button
        size="small"
        variant="outlined"
        color="secondary"
        onClick={onDismiss}
      >
        {t('Dismiss')}
      </Button>
    </Box>
  );

  if (review.status === 'reading') {
    return (
      <Grid size={12}>
        <Box sx={PANEL_SX} data-testid="lab-report-review">
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <CircularProgress size={16} />
            {fromInbox
              ? t('Reading the report from the Lab Report Inbox…')
              : review.individual
              ? t('Reading the lab report for {{individualNoun}}…', {
                  individualNoun: individualNoun(review.individual)
                })
              : t('Reading the lab report…')}
          </Typography>
        </Box>
      </Grid>
    );
  }

  // A report about two or more people: whose this Test is, still to choose (TGCLOUD-1868, E4).
  if (review.status === 'choosing') {
    return (
      <Grid size={12}>
        <Box
          sx={PANEL_SX}
          data-testid="lab-report-review"
          data-status="choosing"
        >
          {title}
          <IndividualChoice
            review={review}
            testScope={testScope}
            onChoose={onChooseIndividual}
            onDismiss={onDismiss}
            onSendToInbox={onSendChoiceToInbox}
          />
        </Box>
      </Grid>
    );
  }

  if (review.status === 'failed' && fromInbox) {
    const error = review.error || {};
    return (
      <Grid size={12}>
        <Box sx={PANEL_SX} data-testid="lab-report-inbox-refused">
          {title}
          <Alert severity="warning" sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {/* One of labReportInboxText's titles: English, translated here. */}
              {t(error.title) ||
                t('The report from the Lab Report Inbox was not read')}
            </Typography>
            <Typography variant="body2">
              {error.message ||
                t('The report from the Lab Report Inbox could not be read.')}
            </Typography>
            {(error.reasons || []).map((reason) => (
              <Typography key={reason} variant="body2">
                {reason}
              </Typography>
            ))}
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {t(
                'Nothing from it has been added to this Test. Resolve it in the Lab Report Inbox, then open the Test from there again.'
              )}
            </Typography>
          </Alert>
          {dismissButton}
        </Box>
      </Grid>
    );
  }

  if (review.status === 'failed') {
    return (
      <Grid size={12}>
        <Box sx={PANEL_SX} data-testid="lab-report-review">
          {title}
          <Alert severity="warning" sx={{ mb: 1 }}>
            {review.error?.message || t('The lab report could not be read.')}
          </Alert>
          {dismissButton}
        </Box>
      </Grid>
    );
  }

  // Read, and waiting: the form holds another report (one report per save).
  if (review.status !== 'applied') {
    return (
      <Grid size={12}>
        <Box
          sx={PANEL_SX}
          data-testid="lab-report-review"
          data-status="waiting"
        >
          {title}
          {forWhomLine}
          <Alert severity="info" sx={{ mb: 1 }} data-testid="other-applied">
            {otherApplied
              ? t(OTHER_APPLIED)
              : t(
                  'This lab report has been read, and is being put on the Test.'
                )}
          </Alert>
          {dismissButton}
        </Box>
      </Grid>
    );
  }

  const reading = review.reading || {};
  const plan = review.application?.plan || {
    fields: [],
    genes: [],
    variants: []
  };
  // The list state the application proposed for the gene list, where it proposed one.
  const listState = review.application?.listState || null;
  const incomplete = Array.isArray(reading.incomplete)
    ? reading.incomplete
    : [];
  const identity = reading.identity || {};
  const identityCheck = identityCheckOf(identity);
  const acknowledgedWarnings = review.acknowledgedWarnings || new Set();
  const choices = review.choices || {};
  // The server's, from the whole reading: the proposal here may withhold the conclusion.
  const warnings = warningsOfReading(reading);
  const conflicts = conflictsOf(plan);
  const notes = incomplete.filter(
    (i) =>
      (incompleteKind(i) === 'note' || incompleteKind(i) === 'names') &&
      !ENTERED_ON_THE_FORM.test(i.path || '')
  );

  const fieldValueText = (field, value) =>
    field === 'dateResultReceived'
      ? value
        ? fmtDate(toLocalDateFromPickerOrImport(value))
        : ''
      : field === 'testScope'
      ? testScopeText(value, t)
      : value || '';

  const fills = plan.fields.filter((f) => f.kind === 'fill');
  const skipped = plan.fields.filter((f) => f.kind === 'skipped');
  // A Test field emptied where the reader was not confident, that the Test held a value at.
  const withheldFields = plan.fields.filter(
    (f) =>
      f.kind === 'withheld' &&
      typeof f.current === 'string' &&
      f.current.trim() !== ''
  );
  const heldValueText = (field, value) =>
    field === 'zygosity'
      ? t(ZYGOSITY_LABELS[value?.value]) || value?.value || ''
      : /classificationDate$/.test(field)
      ? fmtDate(toLocalDate(value))
      : typeof value === 'string'
      ? value
      : '';
  const byKind = (items, kind) => items.filter((i) => i.kind === kind);
  const shownConflicts = showAllConflicts
    ? conflicts
    : conflicts.slice(0, SHOWN);
  const keepAll = () =>
    onChoose(Object.fromEntries(conflicts.map((c) => [c.id, 'current'])));

  return (
    <Grid size={12}>
      <Box sx={PANEL_SX} data-testid="lab-report-review" data-status="applied">
        {title}
        {forWhomLine}
        <Typography
          variant="caption"
          component="div"
          sx={{ color: 'text.secondary', mb: 1 }}
        >
          {fromInbox
            ? `${t(
                'Matched to this person in the Lab Report Inbox, and read from the report stored there.'
              )} `
            : ''}
          {t(
            'What the report says is filled in on this Test below. Check each part against the report and confirm it where it is on the Test.'
          )}
        </Typography>
        {/* The report's own references, as the Lab Report Inbox shows them (TGCLOUD-1868 A6). */}
        <ReportKeys keys={reading.reportKeys} />

        {/* THE IDENTITY CHECK (TGCLOUD-1868, decision 29). Only a real disagreement asks for the
            acknowledgement, listing what disagrees; what is only information (nothing could be
            compared, a given name alone) is said, with no box to tick. */}
        {!identityCheck.legacy &&
          !identityCheck.required &&
          identityCheck.information.length > 0 && (
            <Alert
              severity="info"
              sx={{ mb: 1.5 }}
              data-testid="identity-information"
            >
              {/* Each reason is one of labReportDisplay's sentences: English, translated here. */}
              {identityCheck.information.map((text) => (
                <Typography key={text} variant="body2">
                  {t(text)}
                </Typography>
              ))}
            </Alert>
          )}
        {identityCheck.required && (
          <Alert
            severity="warning"
            sx={{ mb: 1.5 }}
            data-testid="identity-mismatch"
          >
            {identityCheck.legacy ? (
              identityCheckText(identity, t)
            ) : (
              <>
                <Typography variant="body2">
                  {t(IDENTITY_MAY_BE_SOMEONE_ELSES_LEAD)}
                </Typography>
                <Box
                  component="ul"
                  sx={{ m: 0, pl: 2.5 }}
                  data-testid="identity-reasons"
                >
                  {identityCheck.acknowledge.map((text) => (
                    <Typography key={text} component="li" variant="body2">
                      {t(text)}
                    </Typography>
                  ))}
                </Box>
                {identityCheck.information.map((text) => (
                  <Typography
                    key={text}
                    variant="body2"
                    sx={{ mt: 0.5 }}
                    data-testid="identity-information-reason"
                  >
                    {t(text)}
                  </Typography>
                ))}
              </>
            )}
            <FormControlLabel
              sx={{ display: 'flex', mt: 0.5 }}
              control={
                <Checkbox
                  size="small"
                  checked={review.acknowledged === true}
                  onChange={(e) => onAcknowledge(e.target.checked)}
                />
              }
              label={t(IDENTITY_ACKNOWLEDGEMENT)}
            />
            {fromInbox ? (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {t(
                  'It was matched to this person in the Lab Report Inbox. If it is not theirs, close this Test without saving and undo the match in the Lab Report Inbox.'
                )}
              </Typography>
            ) : (
              onSendToInbox && (
                <Box sx={{ mt: 1 }} data-testid="send-to-inbox">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 1
                    }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      onClick={onSendToInbox}
                      disabled={review.sending === true}
                      startIcon={
                        review.sending ? <CircularProgress size={14} /> : null
                      }
                    >
                      {t('Send to the inbox instead')}
                    </Button>
                    <Typography variant="caption" component="div">
                      {t(
                        'Not this person’s report? It goes to the Lab Report Inbox, to be matched to the right patient, and nothing from it is added to this Test.'
                      )}
                    </Typography>
                  </Box>
                  {review.sendError && (
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, fontWeight: 600 }}
                      role="alert"
                    >
                      {review.sendError.message}
                    </Typography>
                  )}
                </Box>
              )
            )}
          </Alert>
        )}
        {identityCheck.legacy &&
          identity.compared === false &&
          !identity.mismatch && (
            <Typography
              variant="caption"
              component="div"
              sx={{ color: 'text.secondary', mb: 1 }}
            >
              {t(
                'The report’s name and date of birth could not be compared with this record. Check them on the report.'
              )}
            </Typography>
          )}

        {warnings.map((key) => (
          <Alert
            key={key}
            severity="warning"
            sx={{ mb: 1.5 }}
            data-testid={`warning-${key}`}
          >
            {t(READING_WARNINGS[key].text)}
            <FormControlLabel
              sx={{ display: 'flex', mt: 0.5 }}
              control={
                <Checkbox
                  size="small"
                  checked={acknowledgedWarnings.has(key)}
                  onChange={(e) => onAcknowledgeWarning(key, e.target.checked)}
                />
              }
              label={t('I have checked the report.')}
            />
          </Alert>
        ))}

        {notes.length > 0 && (
          <Box data-testid="to-complete">
            <Typography variant="body2" sx={SUBHEAD_SX}>
              {t('To complete')}
            </Typography>
            {notes.map((item) => (
              <Typography
                key={`${item.path}-${item.reason}`}
                variant="caption"
                component="div"
                sx={{ color: 'text.secondary' }}
              >
                {incompleteKind(item) === 'names'
                  ? namesPatientText(item, t)
                  : t(NOTE_COPY[item.path]) || item.reason}
              </Typography>
            ))}
          </Box>
        )}

        {/* ── What saving will fill, add and merge ── */}
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {t('Applying to this Test')}
        </Typography>
        <Box data-testid="apply-plan">
          {/* A field's note is one of applyProposal's sentences: English, translated here. */}
          {fills.map((f) => (
            <Typography
              key={f.id}
              variant="body2"
              component="div"
              sx={VERBATIM_SX}
            >
              {f.note
                ? t('Fills {{field}}: {{value}} — {{note}}', {
                    field: t(FIELD_LABELS[f.field]),
                    value: fieldValueText(f.field, f.proposed),
                    note: t(f.note)
                  })
                : t('Fills {{field}}: {{value}}', {
                    field: t(FIELD_LABELS[f.field]),
                    value: fieldValueText(f.field, f.proposed)
                  })}
            </Typography>
          ))}
          {skipped.map((f) => (
            <Typography
              key={f.id}
              variant="body2"
              component="div"
              sx={{ ...VERBATIM_SX, color: 'warning.dark' }}
              data-testid={`skipped-${f.field}`}
            >
              {t(FIELD_LABELS[f.field])}: {t(f.note)}
            </Typography>
          ))}
          {['add', 'fill'].map((kind) => {
            const n = byKind(plan.genes, kind).length;
            const [one, more] = GENE_PLAN_TEXT[kind];
            return n ? (
              <Typography key={kind} variant="body2" component="div">
                {n === 1 ? t(one) : t(more, { count: n })}
              </Typography>
            ) : null;
          })}
          {byKind(plan.variants, 'add').length > 0 && (
            <Typography variant="body2" component="div">
              {t('Adds {{variants}}.', {
                variants: byKind(plan.variants, 'add')
                  .map((v) => variantLabel(v.proposed))
                  .join('; ')
              })}
            </Typography>
          )}
          {byKind(plan.variants, 'merge').map((v) => (
            <Typography key={v.id} variant="body2" component="div">
              {t('Completes {{variantLabel}} from the report.', {
                variantLabel: variantLabel(v.current)
              })}
            </Typography>
          ))}
          {/* What the Test held where the reader was not confident: emptied, to be entered from
              the report (TGCLOUD-1868 B1, D3). Taking the report off gives it back. */}
          {withheldFields.map((f) => (
            <Typography
              key={f.id}
              variant="body2"
              component="div"
              data-testid={`emptied-${f.field}`}
              sx={VERBATIM_SX}
            >
              {t(
                'Empties {{value}}, to be chosen from the report. This Test held: {{fieldValueText}}.',
                {
                  value: t(FIELD_LABELS[f.field]),
                  fieldValueText: fieldValueText(f.field, f.current)
                }
              )}
            </Typography>
          ))}
          {plan.variants
            .filter((v) => Array.isArray(v.cleared) && v.cleared.length)
            .map((v) => (
              <Typography
                key={`${v.id}-cleared`}
                variant="body2"
                component="div"
                data-testid={`emptied-${v.id}`}
                sx={VERBATIM_SX}
              >
                {t(
                  'Empties {{fields}} of {{variant}}, to be entered from the report. This Test held: {{held}}.',
                  {
                    fields: v.cleared
                      .map((c) => t(CLEARED_FIELD_TEXT[c.field]) || c.field)
                      .join(', '),
                    variant: variantLabel(v.current),
                    held: v.cleared
                      .map((c) => heldValueText(c.field, c.value))
                      .join('; ')
                  }
                )}
              </Typography>
            ))}
          {listState?.status && (
            <Typography
              variant="body2"
              component="div"
              data-testid="proposed-list-state"
            >
              {t(
                'Says of the gene list: “{{value}}”. Confirm it under Genes tested.',
                { value: t(GENES_TESTED_LIST_LABELS[listState.status]) }
              )}
            </Typography>
          )}
          {[...plan.fields, ...plan.genes, ...plan.variants].every(
            (i) =>
              i.kind === 'same' ||
              (i.kind === 'withheld' && !withheldFields.includes(i))
          ) && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('This Test already holds everything the report gives.')}
            </Typography>
          )}
        </Box>

        {conflicts.length > 0 && (
          <Box sx={{ mt: 1.5 }} data-testid="conflicts">
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {t('Where the report differs from this Test ({{count}})', {
                count: conflicts.length
              })}
            </Typography>
            <Typography
              variant="caption"
              component="div"
              sx={{ color: 'text.secondary', mb: 1 }}
            >
              {t(
                'The Test keeps its own value until you choose. Choose, for each, whether the Test keeps its value or takes the report’s.'
              )}
            </Typography>
            {conflicts.length > 1 && (
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Button size="small" onClick={keepAll}>
                  {t('Keep all this Test’s')}
                </Button>
              </Box>
            )}
            {shownConflicts.map((item) => {
              if (item.field) {
                return (
                  <Choice
                    key={item.id}
                    item={item}
                    label={t(FIELD_LABELS[item.field])}
                    current={fieldValueText(item.field, item.current)}
                    proposed={fieldValueText(item.field, item.proposed)}
                    choice={choices[item.id]}
                    onChoose={onChoose}
                  />
                );
              }
              if (item.key) {
                return (
                  <Choice
                    key={item.id}
                    item={item}
                    label={t('Gene {{rowSymbolOf}}', {
                      rowSymbolOf: rowSymbolOf(item.proposed)
                    })}
                    current={geneRowText(item.current, t)}
                    proposed={geneRowText(item.proposed, t)}
                    choice={choices[item.id]}
                    onChoose={onChoose}
                  />
                );
              }
              return (
                <Choice
                  key={item.id}
                  item={item}
                  label={t('Variant {{variantLabel}}', {
                    variantLabel: variantLabel(item.current)
                  })}
                  current={variantText(item.current, t)}
                  // What "Use the report's" puts on the Test: the report's side of each
                  // difference, and what the Test's variant lacked (applyProposal.js).
                  proposed={variantText(item.withReport || item.proposed, t)}
                  choice={choices[item.id]}
                  onChoose={onChoose}
                />
              );
            })}
            {conflicts.length > SHOWN && (
              <Button
                size="small"
                onClick={() => setShowAllConflicts((v) => !v)}
              >
                {showAllConflicts
                  ? t('Show fewer')
                  : t('Show all {{count}} differences', {
                      count: conflicts.length
                    })}
              </Button>
            )}
          </Box>
        )}

        <Box sx={{ mt: 1.5 }}>{dismissButton}</Box>
      </Box>
    </Grid>
  );
}
